import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import useForm from 'assets/components/form/hooks/Form';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import { inputStyles } from 'assets/components/inputs/Input';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { switchPlacesInArray } from 'assets/utils/data/Data';
import { isEmptyValue } from 'assets/utils/data/Object';
import { combineStrings } from 'assets/utils/data/String';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { sortBy } from 'lodash';
import EngineType from 'models/enums/EngineType';
import InputType from 'models/enums/InputType';
import ProductDocumentPropertyPath from 'models/enums/ProductDocumentPropertyPath';
import ProductDocumentSignerSource from 'models/enums/ProductDocumentSignerSource';
import SignatureType from 'models/enums/SignatureType';
import { DefaultValueInput } from 'module/productManagement/components/productDocuments/DefaultValueInput';
import { productDocumentIndexRoute } from 'module/productManagement/views/productDocument/ProductDocument.Index';
import { Fragment, useEffect, useState } from 'react';
import ProductDocumentPreview from '../../../../../components/productDocuments/ProductDocumentPreview';
import { useProductProvider } from '../../../Product.Hooks';

export default function ProductEditDocuments() {
  const { lang, translate } = getLocales();
  const {
    form,
    uploadedProductDocuments,
    newProductDocuments,
    product,
    uploadProductDocumentFile,
    deleteProductDocumentFile,
  } = useProductProvider();

  const [documentCategory, setDocumentCategory] = useState<Model.Enum.EngineType>(EngineType.INTERNAL_COMBUSTION);
  const docForm = useForm<Utils.FormData<Model.IProductDocument & { isFileReplaced?: boolean }>>({
    default: {},
    onSubmit: (document) => {
      const index = form.data.productDocuments?.findIndex((p) => p.id === document.id);
      form.data.productDocuments.splice(index, 1, document);
      form.update({
        productDocuments: [...form.data.productDocuments],
      });
      docForm.reload();
    },
  });

  useEffect(() => {
    if (docForm.data.fillablePdfFields) return;
    const doc = product?.productDocuments?.find((d) => d.id === docForm.data.id);
    if (doc) docForm.set(doc);
  }, [product, docForm.data.id]);

  function updateFillabledPdfField(fillablePdfField: Utils.FormData<Model.IProductFillablePdfField>) {
    const index = docForm.data.fillablePdfFields?.findIndex((p) => p.id === fillablePdfField.id);
    docForm.data.fillablePdfFields.splice(index, 1, fillablePdfField);
    docForm.update({
      fillablePdfFields: [...docForm.data.fillablePdfFields],
    });
  }

  function updateSignatureField(signatureField: Utils.FormData<Model.IProductSignatureField>, remove?: boolean) {
    const index = docForm.data.signatureFields?.findIndex((p) => p?.key === signatureField?.key);
    if (remove) {
      if (index >= 0) docForm.data.signatureFields.splice(index, 1);
    } else if (index >= 0) {
      docForm.data.signatureFields.splice(index, 1, signatureField);
    } else {
      docForm.data.signatureFields.push(signatureField);
    }

    docForm.update({
      signatureFields: [...docForm.data.signatureFields],
    });
  }
  const fillablePdfFields = docForm.data.fillablePdfFields?.filter((f) => f.key);
  const signatureFields = docForm.data.signatureFields?.filter((f) => f.key);

  const allFiles = sortBy([...uploadedProductDocuments, ...newProductDocuments], (it) => it.orderNumber);
  function reorderfile(index: number, delta: -1 | 1) {
    form.update({
      productDocuments: switchPlacesInArray(allFiles, index, delta).map((it, i) => ({ ...it, orderNumber: i + 1 })),
    });
  }
  return (
    <>
      <ProductDocumentPreview
        documentPageName={lang.productDocuments}
        children={
          <>
            <div
              style={{
                fontSize: Theme.FontSize.L,
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              {lang.documents}
            </div>
            <div
              style={{
                padding: Theme.Size.M,
                display: docForm.data.id ? 'none' : undefined,
              }}
            >
              <CheckInput
                onChange={({ isForElectricVehicle }) => {
                  setDocumentCategory(isForElectricVehicle ? EngineType.ELECTRIC : EngineType.INTERNAL_COMBUSTION);
                }}
                name="isForElectricVehicle"
                label={lang.isForElectricVehicle}
                class={inputStyles.check.switch}
                value={documentCategory === EngineType.ELECTRIC}
              />
              <br />
              <FileInput
                name="file"
                fileLimit={1}
                onChange={async ({ file }) => {
                  const formFile = file && file[0]?.original;
                  uploadProductDocumentFile({ formFile, documentCategory, orderNumber: allFiles.length + 1 });
                }}
                class={ThemeFileInputDropzone}
                error={docForm.errors.formFile?._objectError}
                disabled={!form.data?.id}
              />
            </div>
          </>
        }
        documents={allFiles.map((it, i) => ({
          id: it.id,
          orderNumber: it.orderNumber,
          url: it.mediaFile.url,
          infoUrl: createPathFromRoute(productDocumentIndexRoute, { id: it?.id }),
          name: it.mediaFile?.name || lang.unknownDocument,
          info: !it.id ? lang.new : lang.uploaded,
          description: it.documentCategory,
          isEditing: docForm.data.id === it.id,
          isDeletable:
            (it.productDealerDocument?.length ?? 0) === 0 && (it.productDealerGroupDocuments?.length ?? 0) === 0,
          onClick: !it.id ? undefined : () => (docForm.data.id === it.id ? docForm.reload() : docForm.set(it)),
          onDelete: !it.id ? undefined : () => deleteProductDocumentFile(it.id),
          onReorder: docForm.data.id ? undefined : (delta) => reorderfile(i, delta),
        }))}
      />
      <br />

      {!isEmptyValue(docForm.data) && (
        <FormWrapper
          class={ThemeWrapperPaper}
          children={
            <>
              <h2>{lang.replaceFile}</h2>
              <FileInput
                name="file"
                fileLimit={1}
                onChange={async ({ file }) => {
                  const formFile = file && file[0]?.original;
                  docForm.update({ formFile, isFileReplaced: true });
                }}
                class={ThemeFileInputDropzone}
                error={docForm.errors.formFile?._objectError}
                disabled={!form.data?.id}
                value={
                  docForm.data.formFile
                    ? [
                        {
                          original: docForm.data.formFile,
                          type: docForm.data.formFile?.type as Utils.File.MimeType,
                          dataUrl: window.URL.createObjectURL(docForm.data.formFile),
                        },
                      ]
                    : undefined
                }
              />
              {fillablePdfFields && (
                <>
                  <h2>{docForm.data.mediaFile?.name}</h2>
                  <DataMap
                    data={fillablePdfFields}
                    render={({ data: fillablePdfField }) => {
                      const signatureField = signatureFields?.find((s) => s?.key === fillablePdfField.key);
                      const fieldType = signatureField
                        ? 'signature'
                        : fillablePdfField.isDisabled
                          ? 'disabled'
                          : 'enabled';
                      return (
                        <Fragment key={fillablePdfField.id}>
                          <SelectInput
                            name="type"
                            data={[
                              { id: 'enabled', title: combineStrings(' | ', fillablePdfField.key, lang.enabled) },
                              { id: 'disabled', title: combineStrings(' | ', fillablePdfField.key, lang.disabled) },
                              { id: 'signature', title: combineStrings(' | ', fillablePdfField.key, lang.signature) },
                            ]}
                            value={fieldType}
                            style={{
                              input:
                                fieldType === 'enabled'
                                  ? {
                                      backgroundColor: Theme.Color.success,
                                      color: 'white',
                                    }
                                  : fieldType === 'disabled'
                                    ? {
                                        backgroundColor: Theme.Color.error,
                                        color: 'white',
                                      }
                                    : {
                                        backgroundColor: Theme.Color.primary,
                                        color: 'white',
                                      },
                            }}
                            onChange={({ type }) => {
                              if (type === 'enabled' || !type) {
                                updateFillabledPdfField({ ...fillablePdfField, isDisabled: false });
                                updateSignatureField(signatureField, true);
                              } else if (type === 'disabled') {
                                updateFillabledPdfField({ ...fillablePdfField, isDisabled: true });
                                updateSignatureField(signatureField, true);
                              } else {
                                updateFillabledPdfField({ ...fillablePdfField, isDisabled: true });
                                updateSignatureField({
                                  key: fillablePdfField.key,
                                  signerSource: ProductDocumentSignerSource['contract.client.email'],
                                  routingOrder: 1,
                                  productDocumentId: docForm.data.id,
                                });
                              }
                            }}
                          />
                          {signatureField && (
                            <InputWrapper>
                              <SelectInput
                                name="signerSource"
                                label={lang.signerSource}
                                data={Object.values(ProductDocumentSignerSource).map((val) => ({
                                  id: val,
                                  title: val,
                                }))}
                                value={signatureField.signerSource}
                                onChange={(updated) => updateSignatureField({ ...signatureField, ...updated })}
                              />
                              <SelectInput
                                name="type"
                                label={lang.type}
                                data={Object.values(SignatureType).map((val) => ({ id: val, title: translate(val) }))}
                                value={signatureField.type}
                                onChange={(updated) => updateSignatureField({ ...signatureField, ...updated })}
                              />
                              <NumberInput
                                name="routingOrder"
                                label={lang.routingOrder}
                                value={signatureField.routingOrder}
                                onChange={(updated) => updateSignatureField({ ...signatureField, ...updated })}
                              />
                            </InputWrapper>
                          )}
                          {!fillablePdfField.isDisabled && (
                            <>
                              <SelectInput
                                name="propertyPath"
                                label={lang.property}
                                data={Object.values(ProductDocumentPropertyPath).map((val) => ({
                                  id: val,
                                  title: val,
                                }))}
                                value={fillablePdfField.propertyPath}
                                onChange={(updated) =>
                                  updateFillabledPdfField({ ...fillablePdfField, ...updated, defaultValue: null })
                                }
                              />
                              <InputWrapper>
                                <SelectInput
                                  name="type"
                                  label={lang.type}
                                  data={Object.values(InputType).map((val) => ({ id: val, title: translate(val) }))}
                                  value={fillablePdfField.type}
                                  onChange={(updated) =>
                                    updateFillabledPdfField({ ...fillablePdfField, ...updated, defaultValue: null })
                                  }
                                />
                                <TextInput
                                  name="group"
                                  label={lang.group}
                                  value={fillablePdfField.group}
                                  onChange={(updated) => updateFillabledPdfField({ ...fillablePdfField, ...updated })}
                                />
                                <TextInput
                                  name="label"
                                  label={lang.label}
                                  value={fillablePdfField.label}
                                  onChange={(updated) => updateFillabledPdfField({ ...fillablePdfField, ...updated })}
                                />
                              </InputWrapper>
                              <DefaultValueInput
                                fillablePdfField={fillablePdfField}
                                onValueChange={(updated) =>
                                  updateFillabledPdfField({ ...fillablePdfField, ...updated })
                                }
                              />
                              <CheckInput
                                name="isRequired"
                                label={lang.isRequired}
                                value={fillablePdfField.isRequired}
                                onChange={(updated) => updateFillabledPdfField({ ...fillablePdfField, ...updated })}
                                class={inputStyles.check.switch}
                              />
                            </>
                          )}
                        </Fragment>
                      );
                    }}
                  />
                </>
              )}
              <Button
                render={lang.save}
                media={<Icon class="fas-save" />}
                onClick={docForm.submit}
                disabled={docForm.hasErrors}
                active={!docForm.hasErrors}
                class={ThemeButtonCircle}
                htmlElementProps={{ title: lang.save }}
              />
            </>
          }
        />
      )}
    </>
  );
}
